import {type PanelConfigOverride} from '../../../components/PanelBank/types';
import {PANEL_BANK_HIDDEN_SECTION_NAME} from '../../../util/panelbankTypes';
import {getKey} from '../../../util/panels';
import {type PanelConfig} from '../../../util/panelTypes';
import type * as PanelTypes from '../panel/types';
import type * as PanelBankSectionConfigTypes from '../panelBankSectionConfig/types';
import {type ViewReducerState} from '../reducerSupport';
import type * as Types from './types';

/**
 * Checks if a panel is eligible for sparse config overrides.
 * A panel is eligible if:
 * - It has a stable key (metric-based identity)
 * - It is marked as auto-generated (isAuto === true)
 * - (When sectionRef provided) The section is not the hidden section
 * - (When sectionRef provided) The section doesn't have legacy customizations
 *
 * Custom panels (without keys or not auto-generated) are not eligible
 * and must be fully persisted.
 */
export function isPanelEligibleForConfigOverride(
  state: ViewReducerState,
  panelRef: PanelTypes.Ref,
  sectionRef?: PanelBankSectionConfigTypes.Ref,
  panelBankConfigRef?: Types.Ref
): {eligible: boolean; panelKey: string | null} {
  const panel = state.parts[panelRef.type]?.[panelRef.id];

  // Must have a stable key
  const panelKey = panel ? getKey(panel) : null;
  if (panelKey == null) {
    return {eligible: false, panelKey: null};
  }

  // Must be auto-generated (custom panels aren't eligible)
  if (panel.isAuto !== true) {
    return {eligible: false, panelKey};
  }

  // Section-level checks (when section context is available)
  if (sectionRef) {
    const section = state.parts[sectionRef.type]?.[sectionRef.id];

    // Panel must actually exist in the section's panelRefs. Copy panels in
    // the panel editor share the same metric key as the original but aren't
    // in any section — they shouldn't create config overrides or affect the
    // section's isPanelsAuto status.
    if (section?.panelRefs) {
      const panelInSection = section.panelRefs.some(
        (ref: {id: string}) => ref.id === panelRef.id
      );
      if (!panelInSection) {
        return {eligible: false, panelKey};
      }
    }
    // Hidden section panels are not eligible.
    // Check both name and position (last section) to be robust.
    if (section?.name === PANEL_BANK_HIDDEN_SECTION_NAME) {
      if (panelBankConfigRef) {
        const pbConfig =
          state.parts[panelBankConfigRef.type]?.[panelBankConfigRef.id];
        const sectionRefs = pbConfig?.sectionRefs;
        if (
          sectionRefs &&
          sectionRefs[sectionRefs.length - 1]?.id === sectionRef.id
        ) {
          return {eligible: false, panelKey};
        }
      } else {
        // Without panel bank context, fall back to name-only check
        return {eligible: false, panelKey};
      }
    }
    // Don't transition legacy sections to sparse mode - they may have
    // customizations without overrides that would be lost on compression
    if (section?.isPanelsAuto === false) {
      return {eligible: false, panelKey};
    }
  }

  return {eligible: true, panelKey};
}

/**
 * Sets the full config on a config override.
 * Default panel configs are already sparse, so we store the full config.
 */
export function mergeConfigOverride(
  existingOverride: PanelConfigOverride | undefined,
  config: PanelConfig
): PanelConfigOverride {
  return {
    ...existingOverride,
    config,
  };
}
